// src/components/RequestFlow/Steps/DataEntry/DataEntryStep.js
import React, { useState, useRef, useEffect } from 'react';
import api from '../../../../services/api';
import NavigationButtons from '../../NavigationButtons';
import PersonalInfo from './Sections/PersonalInfo';
import BankInformation from './Sections/BankInformation';
import AddressContact from './Sections/AddressContact';
import ContactInfo from './Sections/ContactInfo';
import Referrals from './Sections/Referrals';
import EmploymentInfo from './Sections/EmploymentInfo';
import { BANKS } from '../RequestSetupStep';
import Swal from 'sweetalert2';
import { useSuggestedData } from '../../../../context/SuggestedDataContext';
import { trackRequestStepCompleted } from '../../../../analytics/events';
import { useAnalyticsContext } from '../../../../analytics/useAnalyticsContext';
import { fetchDataEntryCompletionStatus } from '../../../../services/comersRequestFlow';
import { getCustomerRouteKey } from '../../../../utils/customerRoute';
import { normalizeEmploymentId } from '../../../../utils/employment';
import './DataEntryContinuous.css';

export const buildEmploymentInfoPayload = (formData) => ({
  companyName: formData.companyName,
  companyIdentification: String(formData.companyIdentification || '').replace(/\D/g, ''),
  entryDate: formData.entryDate,
  position: formData.position,
  workPhone: formData.workPhone,
  workAddress: formData.workAddress,
  workProvinceId: formData.workProvince,
  workLocalityId: formData.workLocality,
  workPostalCode: `${formData.workPostalCode || ''}`,
});

export const isTransientEmploymentSelection = (employmentId) => {
  const normalizedId = normalizeEmploymentId(employmentId);

  return normalizedId?.startsWith('new_') || normalizedId?.startsWith('edited_') || false;
};

export const shouldSendEmploymentInfoOnSubmit = ({ formData, selectedEmployment }) => {
  const selectedId = normalizeEmploymentId(selectedEmployment);

  if (selectedId?.startsWith('new_') || selectedId?.startsWith('edited_')) {
    return true;
  }

  const normalizedTargetEmployment = normalizeEmploymentId(formData?.targetEmployment?.labor_data_id);

  return selectedId !== null
    && normalizedTargetEmployment !== null
    && selectedId === normalizedTargetEmployment;
};

const DataEntryStep = ({ onNext, onBack, formData: initialData = {}, clientData, requestId, demoMode = false, flowVariant = 'initial', productRequirements = null }) => {
  const { suggestedData, setSuggestedData, selectedData, setSelectedData } = useSuggestedData();
  const { commerceId, branchId } = useAnalyticsContext();
  const customerRouteKey = getCustomerRouteKey(clientData);

  const [allowEditPersonalInfo] = useState(process.env.REACT_APP_ALLOW_EDIT_PERSONAL_INFO === 'true');
  
  // Obtener campos de contacto de solo lectura desde variable de entorno
  const readonlyContactFields = process.env.REACT_APP_READONLY_CONTACT_INFO
    ?.split(',')
    .map(f => f.trim())
    .filter(f => f !== '') || [];
  
  // Configuración del modo de visualización
  const displayMode = process.env.REACT_APP_DATA_ENTRY_DISPLAY_MODE || 'accordion'; // 'accordion' | 'continuous'
  const isContinuousMode = displayMode === 'continuous';

  const resolveSelectedBankData = () => {
    if (initialData?.requestSetup?.selectedBank) {
      return initialData.requestSetup.selectedBank;
    }

    if (clientData?.bank) {
      return clientData.bank;
    }

    const selectedBankCode = initialData?.requestSetup?.bank;
    if (!selectedBankCode) {
      return null;
    }

    const matchedBank = BANKS.find((bank) => String(bank.code) === String(selectedBankCode));
    if (!matchedBank) {
      return null;
    }

    return {
      id: matchedBank.id || matchedBank.code,
      code: matchedBank.code,
      name: matchedBank.name
    };
  };
  
  // Estado local para el banco seleccionado
  const [selectedBank, setSelectedBank] = useState(() => resolveSelectedBankData());

  const INITIAL_FLOW_SECTIONS = ['PersonalInfo', 'ContactInfo'];
  const APPROVED_FLOW_SECTIONS = ['PersonalInfo', 'BankInformation', 'AddressContact', 'ContactInfo', 'EmploymentInfo', 'Referrals'];
  const requiresEmployerData = productRequirements?.requires_employer_data !== false;
  const emailRequired = requiresEmployerData;

  // Obtener configuración de secciones habilitadas
  const getEnabledSections = () => {
    let sections;

    if (flowVariant === 'approved') {
      sections = [...APPROVED_FLOW_SECTIONS];
    } else if (demoMode) {
      sections = [...INITIAL_FLOW_SECTIONS];
    } else {
      const configString = process.env.REACT_APP_DATA_ENTRY_STEPS || 'PersonalInfo,AddressContact,ContactInfo,Referrals';
      sections = configString.split(',').map(section => section.trim());
    }

    if (!requiresEmployerData) {
      return sections.filter((section) => section !== 'EmploymentInfo');
    }

    return sections;
  };

  const enabledSections = getEnabledSections();

  const initialPersonalInfo = {
    firstName: initialData?.firstName ?? clientData?.firstName ?? '',
    lastName: initialData?.lastName ?? clientData?.lastName ?? '',
    birthDate: initialData?.birthDate ?? clientData?.birthDate ?? '',
    gender: initialData?.gender ?? clientData?.gender ?? '',
  };

  const hasCompletePersonalInfo = ['firstName', 'lastName', 'birthDate', 'gender'].every((field) => {
    const value = initialPersonalInfo[field];
    return typeof value === 'string' ? value.trim() !== '' : Boolean(value);
  });
  
  // Determinar si se debe mostrar cada sección específica
  const showPersonalInfo = enabledSections.includes('PersonalInfo')
    && !(flowVariant === 'approved' && hasCompletePersonalInfo);
  const showBankSection = enabledSections.includes('BankInformation');
  const showAddressContact = enabledSections.includes('AddressContact');
  const showContactInfo = enabledSections.includes('ContactInfo');
  const showReferrals = enabledSections.includes('Referrals');
  const showEmploymentSection = enabledSections.includes('EmploymentInfo');

  const [formData, setFormData] = useState({
    ...initialPersonalInfo,
    ...(showBankSection && { cbu: '' }),
    referral: [
      { firstName: '', lastName: '', phone: '', relationship: '' },
      { firstName: '', lastName: '', phone: '', relationship: '' }
    ],
    ...initialData,
  });

  const [isLoading, setIsLoading] = useState(false);
  
  // Referencia para prevenir múltiples clics simultáneos
  const isProcessingRef = useRef(false);

  // Inicializar sección activa con la primera sección habilitada
  const getFirstEnabledSection = () => {
    const sectionsInOrder = ['PersonalInfo', 'BankInformation', 'AddressContact', 'ContactInfo', 'Referrals', 'EmploymentInfo'];
    return sectionsInOrder.find(section => {
      switch (section) {
        case 'PersonalInfo': return showPersonalInfo;
        case 'BankInformation': return showBankSection;
        case 'AddressContact': return showAddressContact;
        case 'ContactInfo': return showContactInfo;
        case 'Referrals': return showReferrals;
        case 'EmploymentInfo': return showEmploymentSection;
        default: return false;
      }
    }) || 'PersonalInfo';
  };

  const [activeSection, setActiveSection] = useState(isContinuousMode ? null : getFirstEnabledSection());
  
  // Configurar validez de secciones solo para las habilitadas
  const getInitialSectionValidity = () => {
    const validity = {};
    if (showPersonalInfo) validity.PersonalInfo = null;
    if (showBankSection) validity.BankInformation = null;
    if (showAddressContact) validity.AddressContact = null;
    if (showContactInfo) validity.ContactInfo = null;
    if (showReferrals) validity.Referrals = null;
    if (showEmploymentSection) validity.EmploymentInfo = null;
    return validity;
  };

  const [sectionValidity, setSectionValidity] = useState(getInitialSectionValidity());

  const [serverErrors, setServerErrors] = useState([]);
  const [contactFieldErrors, setContactFieldErrors] = useState({});

  useEffect(() => {
    const resolvedBank = resolveSelectedBankData();
    if (resolvedBank) {
      setSelectedBank((prevBank) => {
        if (
          String(prevBank?.id || '') === String(resolvedBank.id || '')
          && String(prevBank?.code || '') === String(resolvedBank.code || '')
        ) {
          return prevBank;
        }

        return resolvedBank;
      });
    }

    if (initialData?.requestSetup?.bank || initialData?.requestSetup?.selectedBank) {
      setFormData((prevData) => {
        const nextRequestSetup = {
          ...(prevData.requestSetup || {}),
          ...(initialData.requestSetup || {}),
        };

        if (resolvedBank?.code && !nextRequestSetup.bank) {
          nextRequestSetup.bank = resolvedBank.code;
        }

        if (resolvedBank?.id && !nextRequestSetup.selectedBank) {
          nextRequestSetup.selectedBank = resolvedBank;
        }

        const requestSetupChanged = JSON.stringify(prevData.requestSetup || {}) !== JSON.stringify(nextRequestSetup);
        if (!requestSetupChanged) {
          return prevData;
        }

        return {
          ...prevData,
          requestSetup: nextRequestSetup,
        };
      });
    }
  }, [clientData?.bank, initialData?.requestSetup]);

  const personalInfoRef = useRef();
  const bankInformationRef = useRef();
  const addressContactRef = useRef();
  const contactInfoRef = useRef();
  const referralsRef = useRef();
  const employmentInfoRef = useRef();

  const enabledSectionRefs = [
    { ref: personalInfoRef, enabled: showPersonalInfo },
    { ref: bankInformationRef, enabled: showBankSection },
    { ref: addressContactRef, enabled: showAddressContact },
    { ref: contactInfoRef, enabled: showContactInfo },
    { ref: referralsRef, enabled: showReferrals },
    { ref: employmentInfoRef, enabled: showEmploymentSection },
  ];

  const validateAllEnabledSections = () => enabledSectionRefs.every(({ ref, enabled }) => {
    if (!enabled) {
      return true;
    }

    if (!ref.current?.validateSection) {
      return true;
    }

    return ref.current.validateSection();
  });

  // Obtener las secciones habilitadas para sugerencias
  const enabledSuggestedSections = process.env.REACT_APP_SUGGESTED_DATA_ENABLE?.split(',') || [];

  // Agregar estado para rastrear secciones ya cargadas
  const [loadedSections, setLoadedSections] = useState(new Set());
  const [loadingSections, setLoadingSections] = useState({
    personal: false,
    address: false,
    contact: false,
    referral: false,
    employment: false,
    bank: false
  });

  // Efecto para cargar todas las sugerencias en modo continuo
  useEffect(() => {
    if (isContinuousMode) {
      // Cargar sugerencias de todas las secciones habilitadas
      const sectionsToLoad = ['PersonalInfo', 'BankInformation', 'AddressContact', 'ContactInfo', 'Referrals', 'EmploymentInfo'];
      sectionsToLoad.forEach(section => {
        const shouldLoad = () => {
          switch (section) {
            case 'PersonalInfo': return showPersonalInfo;
            case 'BankInformation': return showBankSection;
            case 'AddressContact': return showAddressContact;
            case 'ContactInfo': return showContactInfo;
            case 'Referrals': return showReferrals;
            case 'EmploymentInfo': return showEmploymentSection;
            default: return false;
          }
        };
        
        if (shouldLoad()) {
          loadSuggestedData(section);
        }
      });
    }
  }, [isContinuousMode, showPersonalInfo, showBankSection, showAddressContact, showContactInfo, showReferrals, showEmploymentSection]);

  // Efecto para validación automática en modo continuo
  useEffect(() => {
    if (isContinuousMode) {
      // Validar todas las secciones habilitadas automáticamente
      const validateAllSections = () => {
        const sectionsToValidate = [
          { name: 'PersonalInfo', ref: personalInfoRef, enabled: showPersonalInfo },
          { name: 'BankInformation', ref: bankInformationRef, enabled: showBankSection },
          { name: 'AddressContact', ref: addressContactRef, enabled: showAddressContact },
          { name: 'ContactInfo', ref: contactInfoRef, enabled: showContactInfo },
          { name: 'Referrals', ref: referralsRef, enabled: showReferrals },
          { name: 'EmploymentInfo', ref: employmentInfoRef, enabled: showEmploymentSection }
        ];

        sectionsToValidate.forEach(({ name, ref, enabled }) => {
          if (enabled && ref.current) {
            setTimeout(() => {
              if (ref?.current?.validateSection) {
                ref.current.validateSection();
              }
            }, 100);
          }
        });
      };

      // Validar después de que se carguen las sugerencias
      const timer = setTimeout(validateAllSections, 500);
      return () => clearTimeout(timer);
    }
  }, [isContinuousMode, selectedData, formData, showPersonalInfo, showBankSection, showAddressContact, showContactInfo, showReferrals, showEmploymentSection]);

  // Modificar la función loadSuggestedData
  const loadSuggestedData = async (section) => {
    if (demoMode) return;

    const sectionToEndpoint = {
      'PersonalInfo': 'personal',
      'AddressContact': 'address', 
      'ContactInfo': 'contact',
      'Referrals': 'referral',
      'EmploymentInfo': 'employment',
      'BankInformation': 'bank'
    };

    const key = sectionToEndpoint[section];
    if (!key) return;

    // Verificar si la sección ya fue cargada
    const bankSuggestionEnabled = key === 'bank' && flowVariant === 'approved';
    if (loadedSections.has(key) || (!enabledSuggestedSections.includes(key) && !bankSuggestionEnabled)) return;

    // Activar el estado de carga para la sección
    setLoadingSections(prev => ({
      ...prev,
      [key]: true
    }));

    try {
      const response = await api.get(`/customers/${customerRouteKey}/suggested-data/${key}`, {
        params: (key === 'bank' || key === 'employment') && requestId
          ? { request_uid: requestId }
          : undefined
      });
      if (response.data) {
        setSuggestedData(prev => ({
          ...prev,
          [key]: key === 'bank' ? response.data.data?.bankInfo || null : response.data.data
        }));
        // Marcar la sección como cargada
        setLoadedSections(prev => new Set([...prev, key]));
      }
    } catch (error) {
      console.error(`Error loading suggested ${section} data:`, error);
    } finally {
      // Desactivar el estado de carga para la sección
      setLoadingSections(prev => ({
        ...prev,
        [key]: false
      }));
    }
  };

  const handleChange = (e, options = {}) => {
    const { name, value } = e.target;
  
    let processedValue = value;
    
    // Procesamiento especial para ciertos campos
    if (name === 'phone' || name === 'workPhone') {
      processedValue = value.replace(/^0+/, ''); // Eliminar ceros a la izquierda para teléfono
    }
  
    setFormData((prev) => {
      if (options.isNested && options.arrayIndex !== undefined) {
        const updatedArray = [...prev[options.nestedKey]];
        updatedArray[options.arrayIndex][name] = processedValue;
        return {
          ...prev,
          [options.nestedKey]: updatedArray,
        };
      }
  
      return {
        ...prev,
        [name]: processedValue,
      };
    });
  };  

  const handleSectionClick = (targetSection) => {
    // En modo continuo, no manejar clics de sección
    if (isContinuousMode) {
      return;
    }

    // Verificar que la sección esté habilitada antes de permitir el clic
    const isSectionEnabled = () => {
      switch (targetSection) {
        case 'PersonalInfo': return showPersonalInfo;
        case 'BankInformation': return showBankSection;
        case 'AddressContact': return showAddressContact;
        case 'ContactInfo': return showContactInfo;
        case 'Referrals': return showReferrals;
        case 'EmploymentInfo': return showEmploymentSection;
        default: return false;
      }
    };

    if (!isSectionEnabled()) {
      console.warn(`Section ${targetSection} is not enabled`);
      return;
    }

    let currentSectionRef;

    switch (activeSection) {
      case 'PersonalInfo':
        currentSectionRef = showPersonalInfo ? personalInfoRef : null;
        break;
      case 'BankInformation':
        currentSectionRef = showBankSection ? bankInformationRef : null;
        break;
      case 'AddressContact':
        currentSectionRef = showAddressContact ? addressContactRef : null;
        break;
      case 'ContactInfo':
        currentSectionRef = showContactInfo ? contactInfoRef : null;
        break;
      case 'Referrals':
        currentSectionRef = showReferrals ? referralsRef : null;
        break;
      case 'EmploymentInfo':
        currentSectionRef = showEmploymentSection ? employmentInfoRef : null;
        break;
      default:
        currentSectionRef = null;
    }

    if (currentSectionRef && currentSectionRef.current) {
      currentSectionRef.current.validateSection();
    }

    if (activeSection === targetSection) {
      setActiveSection(null);
    } else {
      setActiveSection(targetSection);
      // Cargar sugerencias cuando se abre una nueva sección
      loadSuggestedData(targetSection);
    } 
  };

  const handleNext = async () => {
    // Prevenir múltiples clics simultáneos
    if (isProcessingRef.current) {
      console.warn('Proceso ya en curso, espere por favor...');
      return;
    }

    if (!validateAllEnabledSections()) {
      console.warn('No puede avanzar sin completar todos los campos requeridos.');
      return;
    }

    const enabledSectionsKeys = Object.keys(sectionValidity);
    const allEnabledSectionsValid = enabledSectionsKeys.every((sectionKey) => sectionValidity[sectionKey] === true);

    if (!allEnabledSectionsValid) {
      console.warn('Por favor, complete todas las secciones antes de continuar.');
      return;
    }

    // Marcar que el proceso está en curso
    isProcessingRef.current = true;
    setIsLoading(true);

    try {
      const success = await handleSubmit();
      if (!success) {
        console.error('No se pudo guardar la información. Por favor, revise los errores.');
        return;
      }

      if (requestId && flowVariant === 'approved') {
        const completionStatus = await fetchDataEntryCompletionStatus(requestId);

        if (!completionStatus?.can_continue) {
          const backendErrors = [
            ...(completionStatus?.missing_requirements || []).map((item) => item.label),
            ...(completionStatus?.invalid_requirements || []).map((item) => item.label),
            ...(completionStatus?.blocking_requirements || []).map((item) => item.label),
          ].filter(Boolean);

          setServerErrors(backendErrors);

          Swal.fire({
            icon: 'warning',
            title: 'Faltan datos para continuar',
            html: backendErrors.length > 0
              ? `<ul style="text-align:left;margin:0;padding-left:1.2rem;">${backendErrors.map((message) => `<li>${message}</li>`).join('')}</ul>`
              : 'El backend detectó datos incompletos o inválidos.',
          });
          return;
        }
      }

      if (commerceId) {
        trackRequestStepCompleted({
          commerceId,
          branchId,
          stepName: 'DataEntryStep',
          customerDni: clientData?.dni
        });
      }

      onNext({
        personalInfo: formData,
        contactPhone: formData.phone
      });
    } catch (error) {
      console.error('Error en handleNext:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo validar la completitud de los datos. Intente nuevamente.',
      });
    } finally {
      // Limpiar el estado de procesamiento
      isProcessingRef.current = false;
      setIsLoading(false);
    }
  };  

  const handleBankChange = (newBankData) => {
    setSelectedBank(newBankData);
  };

  const isPlaceholderContactId = (contactId) => {
    const value = contactId?.toString() || '';
    return value.startsWith('temp_contact_')
      || value.startsWith('temp_phone_')
      || value.startsWith('temp_email_');
  };

  const isTransientContactSelection = (contactId) => {
    const value = contactId?.toString() || '';
    return value.startsWith('new_')
      || value.startsWith('edited_')
      || value.startsWith('contact_')
      || isPlaceholderContactId(value);
  };

  const normalizeEmailForComparison = (email) => (email || '').toString().trim().toLowerCase();
  const normalizePhoneForComparison = (phone) => (phone || '').toString().replace(/\D/g, '');

  const findContactSuggestionById = (contactId) => {
    if (!contactId || !Array.isArray(suggestedData.contact)) {
      return null;
    }

    return suggestedData.contact.find((suggestion) => String(suggestion.id) === String(contactId)) || null;
  };

  const getPrimaryContactSuggestion = (contactType) => {
    if (!Array.isArray(suggestedData.contact)) {
      return null;
    }

    return suggestedData.contact.find((suggestion) => (
      suggestion.contact_type === contactType
      && suggestion.is_primary === true
      && suggestion.is_active !== false
      && !suggestion.deactivated_at
    )) || null;
  };

  const getPersistedContactValues = () => {
    const selectedContact = findContactSuggestionById(selectedData.contact);
    const primaryPhone = getPrimaryContactSuggestion('phone');
    const primaryEmail = getPrimaryContactSuggestion('email');

    return {
      phone: selectedContact?.mobile
        || selectedContact?.phone
        || primaryPhone?.mobile
        || primaryPhone?.phone
        || clientData?.phone
        || '',
      email: selectedContact?.email
        || primaryEmail?.email
        || clientData?.email
        || '',
    };
  };

  const buildContactPayload = () => {
    if (!showContactInfo) {
      return null;
    }

    const contactSelectionIsTransient = isTransientContactSelection(selectedData.contact)
      && !isPlaceholderContactId(selectedData.contact);
    const persistedContact = getPersistedContactValues();
    const hasEmailValue = Object.prototype.hasOwnProperty.call(formData, 'email');
    const hasPhoneValue = Object.prototype.hasOwnProperty.call(formData, 'phone');
    const emailChanged = hasEmailValue
      && normalizeEmailForComparison(formData.email) !== normalizeEmailForComparison(persistedContact.email);
    const phoneChanged = hasPhoneValue
      && normalizePhoneForComparison(formData.phone) !== normalizePhoneForComparison(persistedContact.phone);
    const shouldSendContact = contactSelectionIsTransient || emailChanged || phoneChanged;

    if (!shouldSendContact) {
      return null;
    }

    const trimmedEmail = String(formData.email || '').trim();
    const payload = {
      ...(hasPhoneValue && (!readonlyContactFields.includes('phone') || !clientData?.phone) && { mobile: formData.phone }),
      ...(hasEmailValue && trimmedEmail && (!readonlyContactFields.includes('email') || !clientData?.email) && { email: trimmedEmail }),
    };

    return Object.keys(payload).length > 0 ? payload : null;
  };

  const handleSubmit = async () => {
    if (demoMode) {
      return true;
    }

    if (!validateAllEnabledSections()) {
      return false;
    }

    setServerErrors([]);
    setContactFieldErrors({});

    const contactPayload = buildContactPayload();
    const contactSelectionIsTransient = isTransientContactSelection(selectedData.contact);
    
    const payload = {
      request_id: requestId,
      enabled_sections: enabledSections,
      validation_rules: {
        referrals: {
          min_count: parseInt(process.env.REACT_APP_MIN_REFERRALS || '1')
        }
      },
      existingData: {
        address: (selectedData.address?.toString().startsWith('new_') || selectedData.address?.toString().startsWith('edited_')) ? null : selectedData.address,
        referral: selectedData.referral?.filter(id => !id.toString().startsWith('new_') && !id.toString().startsWith('edited_')) || [],
        contact: contactSelectionIsTransient || contactPayload ? null : selectedData.contact,
        employment: (selectedData.employment?.toString().startsWith('new_') || selectedData.employment?.toString().startsWith('edited_')) ? null : selectedData.employment
      },
      personalInfo: {
        firstname: formData.firstName,
        lastname: formData.lastName,
        gender: formData.gender,
        birthdate: formData.birthDate,
      },
      ...(showBankSection && selectedBank && {
        bankInfo: {
          bank_id: selectedBank?.id,
          cbu: formData.cbu
        }
      }),
      address: (selectedData.address?.toString().startsWith('new_') || selectedData.address?.toString().startsWith('edited_')) ? {
        address: formData.address,
        number: formData.number,
        apartment: formData.apartment || null,
        provinceId: formData.province,
        localityId: formData.locality,
        postalCode: formData.postalCode ? `${formData.postalCode}` : '',
      } : null,
      contact: contactPayload,
      referrals: suggestedData.referral
        ?.filter(referral => 
          selectedData.referral?.includes(referral.id) && 
          (referral.id.toString().startsWith('new_') || referral.id.toString().startsWith('edited_'))
        )
        .map(referral => ({
          firstname: referral.firstName,
          lastname: referral.lastName,
          phone: referral.phone,
          relationship: referral.relationship,
        })) || [],
      ...(requiresEmployerData && showEmploymentSection && {
        employmentInfo: shouldSendEmploymentInfoOnSubmit({
          formData,
          selectedEmployment: selectedData.employment,
        })
          ? buildEmploymentInfoPayload(formData)
          : null,
      }),
    };

    try {
      const response = await api.post(`/customers/${customerRouteKey}/data`, payload);
      if (response.status === 200) {
        return true; 
      }
    } catch (error) {
      console.error('Error updating customer data:', error);
      
      if (error.response?.data?.errors) {
        const fieldErrors = error.response.data.errors;
        const errorMessages = Object.values(fieldErrors)
          .flat()
          .map(msg => msg.replace(/employment info\./g, ''));

        const nextContactFieldErrors = {};
        if (fieldErrors['contact.email_blacklisted']?.[0]) {
          nextContactFieldErrors.email = fieldErrors['contact.email_blacklisted'][0];
        }
        if (fieldErrors['contact.phone_blacklisted']?.[0]) {
          nextContactFieldErrors.phone = fieldErrors['contact.phone_blacklisted'][0];
        }

        setContactFieldErrors(nextContactFieldErrors);
        setServerErrors(errorMessages);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Hubo un problema al guardar los datos. Por favor, intente nuevamente.',
        });
      }
      return false;
    }    
  };  

  return (
    <div className="container rg-request-data-step">
      <h4 className="text-center rg-request-step__title">
        {flowVariant === 'approved' ? 'Completa la información restante' : 'Complete sus datos personales'}
      </h4>

      {/* Contenedor de formularios - modo accordion o continuo */}
      <div className={isContinuousMode ? "continuous-form-container" : "accordion"} id={isContinuousMode ? "datosContinuous" : "datosAccordion"}>
        {showPersonalInfo && (
          <PersonalInfo
            ref={personalInfoRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "PersonalInfo" : activeSection}
            sectionName="PersonalInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.personal}
            displayMode={displayMode}
          />
        )}
        {showBankSection && (
          <BankInformation
            ref={bankInformationRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "BankInformation" : activeSection}
            sectionName="BankInformation"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            bankData={selectedBank}
            suggestedBankInfo={suggestedData.bank}
            onBankChange={handleBankChange}
            clientData={clientData}
            displayMode={displayMode}
          />
        )}
        {showAddressContact && (
          <AddressContact
            ref={addressContactRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "AddressContact" : activeSection}
            sectionName="AddressContact"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.address}
            displayMode={displayMode}
            clientData={clientData}
          />
        )}
        {showContactInfo && (
          <ContactInfo
            ref={contactInfoRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "ContactInfo" : activeSection}
            sectionName="ContactInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.contact}
            displayMode={displayMode}
            clientData={clientData}
            serverFieldErrors={contactFieldErrors}
            emailRequired={emailRequired}
          />
        )}
        {showEmploymentSection && (
          <EmploymentInfo
            ref={employmentInfoRef}
            formData={formData}
            setFormData={setFormData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "EmploymentInfo" : activeSection}
            sectionName="EmploymentInfo"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.employment}
            displayMode={displayMode}
            clientData={clientData}
            targetEmployment={formData.targetEmployment}
          />
        )}
        {showReferrals && (
          <Referrals
            ref={referralsRef}
            formData={formData}
            handleChange={handleChange}
            activeSection={isContinuousMode ? "Referrals" : activeSection}
            sectionName="Referrals"
            sectionValidity={sectionValidity}
            setSectionValidity={setSectionValidity}
            handleSectionClick={handleSectionClick}
            isLoadingSuggestions={loadingSections.referral}
            displayMode={displayMode}
          />
        )}
      </div>

      {serverErrors.length > 0 && (
        <div className="alert alert-warning mt-3" role="alert">
          <strong className="alert-heading"><i class="bi bi-exclamation-triangle me-2"></i>Por favor, corrija los siguientes errores:</strong>
          <ul className="mb-0 ms-3 list-unstyled">
            {serverErrors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <NavigationButtons
        onBack={onBack}
        onNext={handleNext}
        disableNext={!Object.keys(sectionValidity).every((sectionKey) => sectionValidity[sectionKey] === true)}
        isLoading={isLoading}
      />
    </div>
  );
};

export default DataEntryStep;
